// @ts-strict-ignore
import React, { useEffect, useState } from 'react'
import queryString from 'query-string'
import { useTranslation } from 'react-i18next'
import { useLocation } from 'react-router'
import { RecaptchaScoreError } from 'recaptcha/RecaptchaScoreError'
import { RecaptchaTokenError } from 'recaptcha/RecaptchaTokenError'
import { RecaptchaVerificationError } from 'recaptcha/RecaptchaVerificationError'
import { RecaptchaVersion } from 'recaptcha/types'
import { useRecaptcha } from 'recaptcha/useRecaptcha'
import { isRecapthcaLowScoreError, isRecapthcaVerificationError } from 'recaptcha/utils'
import { Box, Button, Callout, Input } from '@node-space/ui-components'
import LoginPageLayout, { LoginPageLayoutVariant } from 'components/layout/LoginPageLayout'
import { PathNames } from 'constants/General'
import { useResetPasswordMutation } from 'hooks/mutations/usePasswordMutation'
import { FingerprintFlows, useFingerprint } from 'hooks/useFingerprint'
import { BaseErrorResponse } from 'types/beneficiaries'
import { applyPasswordTests } from 'utils/password'
import { logError } from 'utils/sentry'

export default function PasswordReset() {
  const { t } = useTranslation()
  const location = useLocation()
  const [showErrorBanner, setShowErrorBanner] = useState(false)
  const [newPassword, setNewPassword] = useState('')
  const [confirmPassword, setConfirmPassword] = useState('')
  const [displayErrors, setDisplayErrors] = useState(false)
  const [newPasswordError, setNewPasswordError] = useState(false)
  const [confirmPasswordError, setConfirmPasswordError] = useState(false)
  const tokenObject = queryString.parse(location.search)
  const defaultErrorMessage = t('oopsSomethingWentWrong')
  const [errorMessage, setErrorMessage] = useState<React.ReactNode>(defaultErrorMessage)
  const validationResult = applyPasswordTests(newPassword, t)

  const { mutate: resetPassword, isPending } = useResetPasswordMutation()
  const { isLoadingRecaptcha, loadRecaptcha, getRecaptchaToken, unloadRecaptcha } =
    useRecaptcha('resetPassword')
  const { getFingerprintEventId } = useFingerprint()

  useEffect(() => {
    loadRecaptcha()
  }, [loadRecaptcha])

  const getNewRecaptchaToken = async (): Promise<string> => {
    return getRecaptchaToken()
      .then(token => token)
      .catch(error => {
        logError('Error from PasswordReset - getRecaptchaToken', error)
        return ''
      })
  }

  const getNewFingerprintEventId = async (): Promise<string> => {
    return getFingerprintEventId(FingerprintFlows.RESET_PASSWORD).catch(error => {
      logError('Error from PasswordReset - getFingerprintEventId', error)
      return ''
    })
  }

  const validateMatch = () => {
    if (newPassword === confirmPassword) {
      setDisplayErrors(false)
      setConfirmPasswordError(false)
    } else {
      setConfirmPasswordError(true)
    }
  }

  const validatePassword = () => {
    setNewPasswordError(!validationResult?.isValid)
  }

  useEffect(() => {
    validateMatch()
    validatePassword()
  }, [confirmPassword, newPassword])

  const submitRequest = async () => {
    const [recaptchaToken, fingerprintEventId] = await Promise.all([
      getNewRecaptchaToken(),
      getNewFingerprintEventId(),
    ])

    if (!recaptchaToken) {
      setErrorMessage(<RecaptchaTokenError />)
      setShowErrorBanner(true)
      return
    }

    resetPassword(
      {
        newPassword,
        token: tokenObject?.token,
        recaptcha: recaptchaToken,
        recaptchaVersion: RecaptchaVersion.V3,
        fingerprintEventId,
      },
      {
        onSuccess: () => {
          unloadRecaptcha()
          window.location.href = PathNames.DASHBOARD
        },
        onError: (error: BaseErrorResponse) => {
          const errorResponseMessage = error?.data?.errorList?.[0]?.message

          if (isRecapthcaLowScoreError(errorResponseMessage)) {
            setErrorMessage(<RecaptchaScoreError />)
          } else if (isRecapthcaVerificationError(errorResponseMessage)) {
            setErrorMessage(<RecaptchaVerificationError />)
          } else if (error.status === 412) {
            error?.data?.errorList?.map(err => {
              if (err?.parameter === 'password' && err?.code === 'complexity') {
                setErrorMessage(err?.message)
              }
            })
          } else {
            setErrorMessage(defaultErrorMessage)
          }
          setShowErrorBanner(true)
        },
      }
    )
  }

  const onSubmit = async () => {
    validateMatch()
    setDisplayErrors(true)
    if (!newPasswordError && !confirmPasswordError) {
      await submitRequest()
    }
  }

  const handleConfirmPasswordChange = password => {
    setConfirmPassword(password.target.value)
  }

  const handleNewPasswordChange = password => {
    setNewPassword(password.target.value)
    validatePassword()
  }

  return (
    <LoginPageLayout title={t('newPasswordHeader')} variant={LoginPageLayoutVariant.NARROW}>
      <Box className="w-full space-y-6">
        {showErrorBanner && <Callout variant="error">{errorMessage}</Callout>}
        <Input
          type="password"
          placeholderText={t('newPassword')}
          error={newPasswordError && displayErrors}
          errorText={validationResult?.message}
          value={newPassword}
          onChange={handleNewPasswordChange}
        />
        <Input
          type="password"
          placeholderText={t('confirmPassword')}
          error={confirmPasswordError && displayErrors}
          errorText={t('matchingPasswordsError')}
          value={confirmPassword}
          onChange={handleConfirmPasswordChange}
        />
        <Button
          disabled={(newPasswordError || confirmPassword) && displayErrors}
          loading={isPending || isLoadingRecaptcha}
          full
          onClick={onSubmit}
        >
          {t('saveNewPassword')}
        </Button>
      </Box>
    </LoginPageLayout>
  )
}
